import { StateCreator } from 'zustand';
import type { AppState } from '../../store/app-state';
import { ProjectionCue } from './types';
import type { ProjectScope } from '../sync/project-scope';
import { markPendingProjectionCueWrite } from '../sync/projection-pending-writes';

export interface ProjectionsSlice {
    projectionCues: ProjectionCue[];
    projectionActions: {
        addProjectionCue: (cue: Partial<ProjectionCue>) => string;
        updateProjectionCue: (id: string, updates: Partial<ProjectionCue>) => void;
        deleteProjectionCue: (id: string) => void;
        reorderProjectionCues: (sceneId: string, oldIndex: number, newIndex: number) => void;
        clearProjections: () => void;
    };
}

function getProjectionProjectScope(state: AppState): ProjectScope | null {
    if (!state.cloud.enabled || !state.cloud.orgId || !state.cloud.projectId) {
        return null;
    }
    return { orgId: state.cloud.orgId, projectId: state.cloud.projectId };
}

export const createProjectionsSlice: StateCreator<AppState, [['zustand/immer', never]], [], ProjectionsSlice> = (set, get) => ({
    projectionCues: [],
    projectionActions: {
        addProjectionCue: (cue) => {
            const id = crypto.randomUUID();
            set(state => {
                state.projectionCues.push({
                    id,
                    sceneId: cue.sceneId || '',
                    cueNumber: cue.cueNumber || '',
                    mediaType: cue.mediaType || 'projection',
                    source: cue.source || '',
                    description: cue.description || '',
                    duration: cue.duration || '',
                    resolution: cue.resolution || '',
                    notes: cue.notes || '',
                    sortOrder: cue.sortOrder ?? state.projectionCues.length,
                    ...cue,
                });
            });
            const scope = getProjectionProjectScope(get());
            if (scope) {
                markPendingProjectionCueWrite(scope, id);
            }
            return id;
        },
        updateProjectionCue: (id, updates) => {
            const scope = getProjectionProjectScope(get());
            let didUpdate = false;
            set(state => {
                const idx = state.projectionCues.findIndex(c => c.id === id);
                if (idx !== -1) {
                    Object.assign(state.projectionCues[idx]!, updates);
                    didUpdate = true;
                }
            });
            if (scope && didUpdate) {
                markPendingProjectionCueWrite(scope, id);
            }
        },
        deleteProjectionCue: (id) => {
            const scope = getProjectionProjectScope(get());
            let didDelete = false;
            set(state => {
                const idx = state.projectionCues.findIndex(c => c.id === id);
                if (idx !== -1) {
                    state.projectionCues[idx]!.deleted = true;
                    state.projectionCues[idx]!.deletedAt = Date.now();
                    didDelete = true;
                }
            });
            if (scope && didDelete) {
                markPendingProjectionCueWrite(scope, id);
            }
        },
        reorderProjectionCues: (sceneId, oldIndex, newIndex) => {
            const scope = getProjectionProjectScope(get());
            const reorderedCueIds: string[] = [];
            set(state => {
                const sceneCues = state.projectionCues
                    .filter(c => !c.deleted && c.sceneId === sceneId)
                    .toSorted((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
                const [moved] = sceneCues.splice(oldIndex, 1);
                sceneCues.splice(newIndex, 0, moved!);

                for (const [i, c] of sceneCues.entries()) {
                    const storeIdx = state.projectionCues.findIndex(s => s.id === c.id);
                    if (storeIdx !== -1) {
                        state.projectionCues[storeIdx]!.sortOrder = i;
                        reorderedCueIds.push(c.id);
                    }
                }
            });
            if (scope) {
                for (const id of reorderedCueIds) {
                    markPendingProjectionCueWrite(scope, id);
                }
            }
        },
        clearProjections: () => {
            set(state => { state.projectionCues = []; });
        },
    },
});
